// PrivacyPolicy.js
import React from "react";
import "../tearm&conditions/tearm&conditions.css";
import Header from "../../components/Navbar";
import Footer from "../../components/footer/footer";

function PrivacyPolicy() {
  return (
    <>
      <Header />
      <div className="max-w-5xl mx-auto p-6 text-gray-800 leading-relaxed">
        {/* Title */}
        <h1 className="text-2xl font-bold text-center mb-2">
          PRIVACY POLICY
        </h1>
        <p className="text-center text-gray-600 mb-6">
          Effective: September 03, 2025
        </p>

        <div className="h-[75vh] overflow-y-auto pr-4 space-y-4">
          <p>
            Twoffers (“Twoffers,” “we,” “our,” or “us”) is committed to
            protecting your privacy. This Privacy Policy explains how we collect,
            use, share, and safeguard personal data when you use our website
            [https://www.twoffers.com], related services, mobile applications,
            and affiliated websites and apps (together, the “Sites” or
            “Platform”).
          </p>

          <p>
            This Policy also describes your rights regarding your personal data
            and how you can exercise them. Additional privacy policies or terms
            may apply depending on the specific product, service, or region in
            which you interact with Twoffers. Please review all applicable
            notices when using our Sites.
          </p>

          <p>
            For the purposes of this Policy, “personal data” means information
            that identifies, relates to, or could reasonably be linked to an
            individual, such as name, email, phone number, account details,
            payment data, or online identifiers.
          </p>

          <p>
            If you reside in a jurisdiction with specific privacy laws (such as
            the EEA/UK under GDPR or U.S. states like California, Virginia, or
            Colorado), please see the relevant sections below.
          </p>

          <h2 className="text-xl font-semibold mt-6">1. Information We Collect</h2>
          <p>
            We may collect personal data in the following ways:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><b>(a) Information You Provide Directly:</b></li>
            <ul className="list-decimal pl-8 space-y-1">
              <li>Registration/account creation: name, email, phone number, address, company details, profile information.</li>
              <li>Transactional activity: offers, purchases, or other participation on the Platform.</li>
              <li>Payment data: billing address, card details (processed securely via third-party providers).</li>
              <li>Communications: inquiries to customer support, feedback, surveys, or emails.</li>
              <li>Applications: if you apply for employment, vendor opportunities, or partnerships, we may collect application-related information.</li>
            </ul>

            <li><b>(b) Information from Third Parties:</b></li>
            <ul className="list-decimal pl-8 space-y-1">
              <li>Business partners, brokers, or service providers assisting with transactions.</li>
              <li>Public databases or verification providers for identity/fraud prevention.</li>
              <li>Marketing partners to tailor communications and offers.</li>
            </ul>

            <li><b>(c) Information Collected Automatically:</b></li>
            <ul className="list-decimal pl-8 space-y-1">
              <li>IP address, browser type, device identifiers, operating system.</li>
              <li>Pages visited, links clicked, time spent on the Site.</li>
              <li>Referring/exit URLs, search queries, and session data.</li>
            </ul>
            <p>This helps us secure the Platform, improve services, and deliver relevant advertising.</p>
          </ul>

          <h2 className="text-xl font-semibold mt-6">2. How We Use Personal Data</h2>
          <p>We may use your information to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide and manage our services (including Listings, offers, or property listings).</li>
            <li>Facilitate transactions and verify payments.</li>
            <li>Customize your experience and improve site performance.</li>
            <li>Provide customer service and respond to requests.</li>
            <li>Send service-related updates, confirmations, and notifications.</li>
            <li>Market products, services, or promotions (with opt-out available).</li>
            <li>Prevent fraud, verify identity, and ensure platform security.</li>
            <li>Comply with legal obligations and enforce our Terms & Conditions.</li>
            <li>Conduct analytics and improve our marketing and advertising.</li>
            <li>For any other purpose disclosed at the point of collection or with your consent.</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6">3. How We Share Personal Data</h2>
          <p>We may share information with:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Affiliates: to provide integrated services and support.</li>
            <li>Service Providers: payment processors, hosting providers, analytics firms, marketing partners, customer support vendors—only as necessary to perform services.</li>
            <li>Business Partners or Sellers: if you engage in transactions via Twoffers.</li>
            <li>Legal Authorities: to comply with law, court orders, or to protect rights and safety.</li>
            <li>Transaction Successors: in the event of a merger, acquisition, or sale of Twoffers assets.</li>
            <li>Aggregated/De-identified Data: which does not identify individuals, for analytics or industry reporting.</li>
          </ul>
          <p>We do not sell personal data.</p>

          <h2 className="text-xl font-semibold mt-6">4. Cookies & Tracking Technologies</h2>
          <p>We use cookies and similar tools to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Recognize returning users and save preferences.</li>
            <li>Authenticate logins and secure accounts.</li>
            <li>Analyze traffic, usage, and campaign effectiveness.</li>
            <li>Deliver personalized content and ads.</li>
          </ul>
          <p>
            You may manage cookies through browser settings, though some features
            may not function properly. Our Sites are not currently configured to
            respond to “Do Not Track” signals.
          </p>

          <h2 className="text-xl font-semibold mt-6">5. Data Transfers</h2>
          <p>
            Your information may be stored and processed outside your country,
            including in jurisdictions with different data protection standards.
            Where required, we use safeguards such as Standard Contractual Clauses
            (SCCs) or equivalent legal protections.
          </p>

          <h2 className="text-xl font-semibold mt-6">6. Data Retention & Security</h2>
          <p>
            We retain personal data as long as necessary to provide services,
            comply with obligations, resolve disputes, and enforce agreements. We
            use reasonable administrative, technical, and physical safeguards to
            protect information. However, no system can be guaranteed 100% secure;
            please take care with your account credentials.
          </p>

          <h2 className="text-xl font-semibold mt-6">7. Children’s Privacy</h2>
          <p>
            The Platform is not intended for children under 18 (or age of majority
            in your jurisdiction). We do not knowingly collect data from minors. If
            we discover such data, we will delete it.
          </p>

          <h2 className="text-xl font-semibold mt-6">8. Your Privacy Rights</h2>
          <p>
            Depending on your location, you may have rights such as:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access: Know what personal data we hold.</li>
            <li>Correction: Request updates to inaccurate information.</li>
            <li>Deletion: Ask us to erase your data, subject to legal exceptions.</li>
            <li>Portability: Request a copy in a structured, machine-readable format.</li>
            <li>Restriction/Objection: Limit or object to certain processing.</li>
            <li>Opt-out: From direct marketing communications.</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6">U.S. State Privacy Rights</h2>
          <p>
            Residents of California, Virginia, Colorado, Connecticut, Utah,
            Texas, Oregon, and other states with privacy laws may request:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Disclosure of categories and specific pieces of personal data collected.</li>
            <li>Deletion of personal data (with exceptions).</li>
            <li>Correction of inaccuracies.</li>
            <li>Non-discrimination for exercising privacy rights.</li>
          </ul>
          <p>
            To exercise rights, contact us at: privacy@twoffers.com.
          </p>

          <h2 className="text-xl font-semibold mt-6">9. Links & Social Media</h2>
          <p>
            The Sites may link to third-party websites. We are not responsible for
            their privacy practices. Please review their privacy policies before
            providing data. Our services may include integrations with social media
            platforms. Your activity on those platforms is governed by their policies.
          </p>

          <h2 className="text-xl font-semibold mt-6">10. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes
            in our practices, technology, or legal requirements. Any updates will
            be posted on this page with an updated “Effective Date.” We encourage
            you to review this Policy periodically.
          </p>

          <h2 className="text-xl font-semibold mt-6">11. Contact Us</h2>
          <p>
            If you have any questions or concerns about this Privacy Policy, please
            contact us at:
          </p>
          <p>Email: privacy@twoffers.com</p>
          <p>Phone: [Your Contact Number]</p>
          <p>Address: [Your Physical Address]</p>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default PrivacyPolicy;
